import { useEffect, useRef, useState, useCallback } from 'react';

const WS_URL = process.env.REACT_APP_WS_URL || `ws://${window.location.host}/ws`;

/*
|--------------------------------------------------------------------------
| useProjectActivity
|--------------------------------------------------------------------------
| Opens a WebSocket connection scoped to a project (ws?jobId=<projectId>,
| same query param name the job-progress sockets use elsewhere in the app)
| and streams "activity" events — an audit log of everything that happens
| on the project (resources added/removed, users assigned/unassigned,
| comments posted, lock/unlock, etc).
|
| Each message looks like:
| {
|   "type": "activity",
|   "activity": {
|     "id": 42,
|     "projectId": "proj_123",
|     "userId": 1,
|     "userName": "Jane Doe",
|     "userEmail": "jane@company.com",
|     "action": "resource_added",
|     "entityType": "resource",
|     "entityId": "page_456",
|     "details": { "resourceName": "Sales Overview", "resourceType": "Page" },
|     "createdAt": "2026-08-30T10:00:00Z"
|   },
|   "timestamp": "..."
| }
|
| GET /projects/:id now returns its own `activity` array (the history up
| to page load) alongside resources/users/comments. Pass that array in as
| `initialActivity` so the log is populated immediately instead of
| starting empty and waiting for the first live event; the WebSocket then
| appends anything new on top of it, and re-syncs (merging, not
| replacing) whenever `initialActivity` changes on a refetch.
|
| Returns the accumulated list of activity entries (newest first),
| connection state, and a manual disconnect/clear helper.
|--------------------------------------------------------------------------
*/

export function useProjectActivity(projectId, initialActivity = []) {

  const wsRef = useRef(null);
  const [activity, setActivity] = useState(initialActivity);
  const [isConnected, setIsConnected] = useState(false);

  // GET /projects/:id now returns its own `activity` array (the history up
  // to the point the page loaded). Whenever that array changes — i.e. a
  // fresh fetch/refetch of the project — resync it into local state,
  // merging in anything the live socket already streamed in that isn't
  // present in the fetched list yet (avoids a refetch momentarily wiping
  // out activity the socket just delivered).
  useEffect(() => {

    if (!Array.isArray(initialActivity) || !initialActivity.length) return;

    setActivity(prev => {

      const known = new Map(initialActivity.map(entry => [entry.id, entry]));

      // Keep any live-streamed entries not yet reflected in the fetched
      // list (e.g. arrived between the last fetch and this one).
      prev.forEach(entry => {
        if (!known.has(entry.id)) {
          known.set(entry.id, entry);
        }
      });

      return Array.from(known.values()).sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      });

    });

  }, [initialActivity]);

  const connect = useCallback(() => {

    if (!projectId || wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(`${WS_URL}?jobId=${projectId}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      console.log(`[WS] Connected to project activity feed ${projectId}`);
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);

        if (msg.type === 'activity' && msg.activity) {
          setActivity(prev => {
            // De-dupe by activity.id in case of reconnect replay.
            if (prev.some(entry => entry.id === msg.activity.id)) {
              return prev;
            }
            return [msg.activity, ...prev];
          });
        }
      } catch (e) {
        console.error('[WS] Project activity parse error:', e);
      }
    };

    ws.onerror = () => {
      console.warn('[WS] Project activity connection error');
    };

    ws.onclose = () => {
      setIsConnected(false);
      console.log(`[WS] Project activity feed closed for ${projectId}`);
    };

  }, [projectId]);

  useEffect(() => {
    connect();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  const disconnect = useCallback(() => {
    wsRef.current?.close();
  }, []);

  const clear = useCallback(() => {
    setActivity([]);
  }, []);

  return { activity, isConnected, disconnect, clear };
}
